import {LocalDate} from '@js-joda/core';
import {useSnackbar} from 'notistack';
import React, {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import useApiFetch, {apiEndpoint} from '../../Auth/Api';
import {getEightWeeks, Holiday, Week} from "./EmployeeSchedulesCompiler";

export type MergedCapacityWithAverages =  {
    associateID : number;
    fiscalMonthReservedCapacity : number[];
    fiscalMonthRemainingCapacity : number[];
    fiscalMonthCapacityPerDay : number[];
};

export type CapacityContext = {
    weeks : Week[];
    capacityData : MergedCapacityWithAverages[] | null;
    holidays : Holiday[] | null;
};

const CapacityContext = createContext<CapacityContext | null>(null);

type Props = {
    startDate : LocalDate;
    associateIDs : number[];
    children : ReactNode;
};

const CapacityContextProvider : React.FC<Props> = ({
    startDate,
    associateIDs,
    children,
} : Props) => {
    const apiFetch = useApiFetch();
    const {enqueueSnackbar} = useSnackbar();
    const [capacityData, setCapacityData] = useState<MergedCapacityWithAverages[] | null>(null);
    const weeks = useMemo(() => getEightWeeks(startDate), [startDate]);
    const [holidays, setHolidays] = useState<Holiday[] | null>(null);

    // Fetch capacity and holiday data. The mounted flag prevents state updates
    // after the component unmounts, which can happen when navigating away
    // before the API calls complete.
    useEffect(() => {
        let mounted = true;

        const fetchData = async () => {
            try {
                const capacityUrl = new URL('/v1/capacity/capacity/scheduling', apiEndpoint);
                capacityUrl.searchParams.set('dateStart', startDate.toString());
                capacityUrl.searchParams.set('dateEnd', startDate.plusWeeks(7).toString());

                const holidaysUrl = new URL('/v1/scheduler/holidays', apiEndpoint);
                holidaysUrl.searchParams.set('dateStart', startDate.toString());
                holidaysUrl.searchParams.set('dateEnd', startDate.plusWeeks(7).toString());

                const [capacityResponse, holidaysResponse] = await Promise.all([
                    apiFetch(capacityUrl.toString(), {
                        method: 'POST',
                        body: JSON.stringify({associateIds: associateIDs}),
                    }),
                    apiFetch(holidaysUrl.toString()),
                ]);

                if (!mounted) {
                    return;
                }

                if (!capacityResponse.ok) {
                    enqueueSnackbar('Could not fetch capacity schedule data.', {variant: 'error'});
                } else {
                    setCapacityData(await capacityResponse.json());
                }

                if (!holidaysResponse.ok) {
                    enqueueSnackbar('Could not load holidays', {variant: 'error'});
                } else {
                    setHolidays(await holidaysResponse.json());
                }
            } catch {
                if (mounted) {
                    enqueueSnackbar('Failed to load capacity data', {variant: 'error'});
                }
            }
        };

        fetchData();

        return () => {
            mounted = false;
        };
    }, [apiFetch, associateIDs, enqueueSnackbar, startDate]);

    const value = useMemo(() => ({
        weeks: weeks,
        capacityData: capacityData,
        holidays: holidays,
    }), [capacityData, holidays, weeks]);

    return (
        <CapacityContext.Provider value={value}>
            {children}
        </CapacityContext.Provider>
    );
};

export const useCapacityContext = () : CapacityContext => {
    const context = useContext(CapacityContext);

    if (!context) {
        throw new Error('Capacity context used out of scope');
    }

    return context;
};

export default CapacityContextProvider;
